import React, { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  TextField,
  Box,
  Typography,
  IconButton
} from "@material-ui/core";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Visibility from "@material-ui/icons/Visibility";

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  otpContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    alignItems: 'center',
  },
  otpInput: {
    width: '40px',
    height: '40px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
    '& .MuiOutlinedInput-input': {
      textAlign: 'center',
      fontSize: '1.2rem',
      fontWeight: '500',
      padding: theme.spacing(1),
    },
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    textAlign: 'center',
  },
  eyeButton: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5),
  },
}));

const VerificationOtp = ({ onVerify, loading }) => {
  const classes = useStyles();
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const inputRefs = useRef([]);

  // Check if OTP is valid (6 digits)
  const isValidOtp = (otpValue) => {
    return /^\d{6}$/.test(otpValue);
  };

  // Check if form is valid
  const isFormValid = () => {
    return otpDigits.every(d => d !== '');
  };

  const handleChange = (index, value) => {
    if (value.length > 1) return;
    if (!/^\d?$/.test(value)) return;
    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = [...otpDigits];
    for (let i = 0; i < pasted.length && i < 6; i++) next[i] = pasted[i];
    setOtpDigits(next);
    const nextEmpty = next.findIndex((d) => !d);
    inputRefs.current[(nextEmpty === -1 ? 5 : nextEmpty)]?.focus();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setHasTriedSubmit(true);
    
    if (!isFormValid()) {
      return;
    }
    
    onVerify(otpDigits.join(''));
  };

  const toggleShowOtp = () => {
    setShowOtp(!showOtp);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box className={classes.container}>
        <Box className={classes.otpContainer}>
          {otpDigits.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              variant="outlined"
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              type={showOtp ? 'text' : 'password'}
              inputProps={{ maxLength: 1 }}
              error={hasTriedSubmit && !digit}
              className={classes.otpInput}
            />
          ))}
          <IconButton
            onClick={toggleShowOtp}
            className={classes.eyeButton}
            size="small"
          >
            {showOtp ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </Box>

        {hasTriedSubmit && !isFormValid() && (
          <Typography className={classes.errorText}>Please enter all 6 digits</Typography>
        )}

        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={!isFormValid() || loading}
        >
          {loading ? 'Verifying...' : 'Verify'}
        </Button>
      </Box>
    </form>
  );
};

export default VerificationOtp;
